import { caseApiRequest } from './apiClient';
import type { ApiResult } from './types';

export type CaseRequestAuditLogEntry = {
  id: string;
  caseRequestId: string;
  createdAt: string;
  action: string;
  actionDisplayName: string;
  userId: string;
  userDisplayName: string;
  userTitle: string;
  userRole: string;
  userDivision: string;
};

export type CaseRequestAuditLogsResponse = {
  caseRequestId: string;
  totalCount: number;
  entries: CaseRequestAuditLogEntry[];
};

function strField(obj: Record<string, unknown>, ...keys: string[]): string {
  for (const k of keys) {
    const v = obj[k];
    if (typeof v === 'string' && v.trim()) return v.trim();
  }
  return '';
}

function normalizeEntry(raw: unknown, index: number, fallbackCaseRequestId: string): CaseRequestAuditLogEntry {
  const r = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
  return {
    id: strField(r, 'id') || `audit-${index}`,
    caseRequestId:
      strField(r, 'caseRequestId', 'case_request_id') || fallbackCaseRequestId,
    createdAt: strField(r, 'createdAt', 'created_at'),
    action: strField(r, 'action') || 'UNKNOWN',
    actionDisplayName: strField(r, 'actionDisplayName', 'action_display_name') || strField(r, 'action') || 'Activity',
    userId: strField(r, 'userId', 'user_id'),
    userDisplayName: strField(r, 'userDisplayName', 'user_display_name') || 'System',
    userTitle: strField(r, 'userTitle', 'user_title'),
    userRole: strField(r, 'userRole', 'user_role'),
    userDivision: strField(r, 'userDivision', 'user_division'),
  };
}

function normalizeResponse(raw: unknown, fallbackCaseRequestId: string): CaseRequestAuditLogsResponse {
  const r = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
  const entriesRaw = r.entries;
  const entriesArr = Array.isArray(entriesRaw) ? entriesRaw : [];
  const caseRequestId = strField(r, 'caseRequestId', 'case_request_id') || fallbackCaseRequestId;
  const totalCountRaw = r.totalCount ?? r.total_count;
  const totalCount =
    typeof totalCountRaw === 'number' && Number.isFinite(totalCountRaw) ? totalCountRaw : entriesArr.length;
  return {
    caseRequestId,
    totalCount,
    entries: entriesArr.map((e, i) => normalizeEntry(e, i, caseRequestId)),
  };
}

/** CMS-CRH-4 — GET /v1/case-requests/{caseRequestId}/audit-logs */
export async function getCaseRequestAuditLogs(
  caseRequestId: string,
  signal?: AbortSignal,
): Promise<ApiResult<CaseRequestAuditLogsResponse>> {
  const id = caseRequestId.trim();
  if (!id) return { success: false, message: 'Case request id is required.' };

  const res = await caseApiRequest<unknown>(`/${encodeURIComponent(id)}/audit-logs`, { signal });
  if (!res.ok) return { success: false, message: res.message };
  return { success: true, data: normalizeResponse(res.data, id) };
}

